import React, { useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import InventorySourceSelector from "./shared/InventorySourceSelector";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";


function MissingSkusTool() {
  const [inventorySource, setInventorySource] = useState("upload");
  const [inventoryShopifyConfig, setInventoryShopifyConfig] = useState({
    location: "Main Store",
    vendors: "",
    excludeArchive: true,
    excludeRS: true
  });
  const [inventoryFile, setInventoryFile] = useState(null);
  const [fetchingShopify, setFetchingShopify] = useState(false);
  const [skuCodesFile, setSkuCodesFile] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const toolInfo = {
    toolName: "Find Missing SKUs",
    description: "Identify SKUs that are present in your SKU Codes file but missing from your Inventory.",
    steps: [
      "Choose your inventory source using Upload Excel or Shopify Sync.",
      "Upload your SKU Codes file (Excel).",
      "Enter the Sheet Name for the SKU Codes file.",
      "Click 'Find Missing SKUs' to download the report.",
    ],
    templates: [
      { name: "Inventory Template", link: "/templates/download/template_inventory.xlsx" },
      { name: "SKU Codes Template", link: "/templates/download/template_sku_codes.xlsx" },
    ],
  };

  const guideTips = [
    "Use the current inventory export so the missing SKU list reflects what is truly absent right now.",
    "The SKU codes tab name must match the workbook tab you want the tool to compare against inventory.",
    "This is especially useful after SKU Builder or vendor updates when you want to catch gaps before ordering.",
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!skuCodesFile || !sheetName) {
      addToast("Please upload the SKU codes file and enter the sheet name.", "error");
      return;
    }

    if (inventorySource === "upload" && !inventoryFile) {
      addToast("Please upload an inventory file or choose Shopify Sync.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (
      (inventorySource === "upload" && inventoryFile && inventoryFile.size > maxSize) ||
      skuCodesFile.size > maxSize
    ) {
      addToast("One or more files are too large. Maximum size is 100MB.", "error");
      return;
    }

    let finalInventoryFile = inventoryFile;

    if (inventorySource === "shopify") {
      setFetchingShopify(true);
      try {
        const excludeTags = [];
        if (inventoryShopifyConfig.excludeArchive) excludeTags.push("Archive");
        if (inventoryShopifyConfig.excludeRS) excludeTags.push("RS");

        const vendors = inventoryShopifyConfig.vendors
          ? inventoryShopifyConfig.vendors.split(",").map((vendor) => vendor.trim()).filter(Boolean)
          : [];

        const locationNames =
          inventoryShopifyConfig.location === "All"
            ? []
            : [inventoryShopifyConfig.location];

        const shopifyRes = await flaskApi.post(
          "/shopify/fetch-inventory-excel",
          { vendors, exclude_tags: excludeTags, location_names: locationNames },
          { responseType: "blob" }
        );

        finalInventoryFile = new File([shopifyRes.data], "shopify_inventory.xlsx", {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
      } catch (error) {
        setFetchingShopify(false);
        const errorMessage = await extractErrorMessage(error, "Failed to fetch inventory from Shopify.");
        addToast(errorMessage, "error");
        return;
      }
      setFetchingShopify(false);
    }

    const formData = new FormData();
    formData.append("inventory_file", finalInventoryFile);
    formData.append("sku_codes_file", skuCodesFile);
    formData.append("sku_sheet_name", sheetName);

    setLoading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post("/purchase/find-missing-skus", formData, {
        responseType: "blob",
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (
        response.data.type === "application/json" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("application/json"))
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        addToast(`Error: ${json.error}`, "error");
        return;
      }

      if (
        response.data.type === "text/html" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("text/html"))
      ) {
        const text = await response.data.text();
        addToast("Server Error: Received HTML instead of file.", "error");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      const filename = extractedFilename || "Missing_SKUs.xlsx";

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      addToast("File downloaded successfully.", "success");
    } catch (error) {
      const errorMessage = await extractErrorMessage(error, "Error generating missing SKUs report.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <ToolPageLayout
        title="Find Missing SKUs"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form tool-form-split">
          <div className="tool-form-main">
          <section className="tool-section">
            <div className="tool-section-header">
              <h3 className="tool-section-title">SKU Codes Workbook</h3>
            </div>

            <div className="tool-field-stack">
              <ToolFileUploadField
                label="SKU Codes Workbook"
                file={skuCodesFile}
                setFile={setSkuCodesFile}
              />
            </div>
          </section>

          <section className="tool-section">
            <div className="tool-section-header">
              <h3 className="tool-section-title">Comparison Settings</h3>
            </div>

            <div className="tool-field-stack">
              <label className="tool-label">SKU Codes Tab Name</label>
              <input
                type="text"
                className="tool-input"
                placeholder="Enter sheet name"
                value={sheetName}
                onChange={(event) => setSheetName(event.target.value)}
                required
              />
            </div>
          </section>

          <div className="tool-action-stack">
            <button type="submit" className="tool-btn-primary" disabled={loading || fetchingShopify}>
              {loading || fetchingShopify ? "Processing..." : "Find Missing SKUs"}
            </button>

            <ToolProgressPanel
              loading={loading}
              isDownloadingShopify={fetchingShopify}
              uploadProgress={uploadProgress}
              uploadMessage="Uploading inventory and SKU codes files..."
              processingMessage="Finding Missing SKUs..."
            />
          </div>
          </div>

          <div className="tool-form-side">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Inventory Source</h3>
              </div>
              <InventorySourceSelector
                source={inventorySource}
                onSourceChange={setInventorySource}
                uploadFile={inventoryFile}
                onUploadFileChange={setInventoryFile}
                shopifyConfig={inventoryShopifyConfig}
                onShopifyConfigChange={setInventoryShopifyConfig}
              />
            </section>
          </div>
        </form>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default MissingSkusTool;
